/**
 * iter 73.10+ — Minimal Tiptap-based WYSIWYG for AnnouncementManagerDrawer.
 *
 * Uses `@tiptap/react` + StarterKit + Link + Placeholder. The output
 * HTML is passed to the backend where it's sanitised again with bleach
 * before storage — belt-and-braces so a malicious tiptap extension
 * can't smuggle disallowed tags past us.
 *
 * Toolbar is intentionally minimal (bold/italic/underline/link/list)
 * to keep the mental model aligned with the server-side allow-list.
 */
import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, LinkSimple } from "@phosphor-icons/react";

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,          // banner shouldn't have headings
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'e.g. Scheduled maintenance Sat 10pm–11pm ET…',
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Sync external value changes (e.g. drawer re-hydrating on edit-mode open)
  // into the editor without clobbering the caret while the user is typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [editor, value]);

  if (!editor) return null;

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const btn = (active, onClick, Icon, label) => (
    <button
      type="button"
      onClick={onClick}
      className={`btn btn-ghost btn-xs ${active ? "bg-primary/15 text-primary" : ""}`}
      title={label}
      data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
    ><Icon size={12} /></button>
  );

  return (
    <div className="border border-border rounded-md">
      <div className="flex items-center gap-1 px-1.5 py-1 border-b border-border bg-muted/30" data-testid="rte-toolbar">
        {btn(editor.isActive("bold"),      () => editor.chain().focus().toggleBold().run(),      TextB,       "Bold")}
        {btn(editor.isActive("italic"),    () => editor.chain().focus().toggleItalic().run(),    TextItalic,  "Italic")}
        {btn(editor.isActive("strike"),    () => editor.chain().focus().toggleStrike().run(),    TextUnderline, "Underline")}
        <div className="w-px h-4 bg-border mx-1" />
        {btn(editor.isActive("bulletList"),  () => editor.chain().focus().toggleBulletList().run(),  ListBullets, "Bulleted list")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListNumbers, "Numbered list")}
        <div className="w-px h-4 bg-border mx-1" />
        {btn(editor.isActive("link"), promptLink, LinkSimple, "Link")}
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none dark:prose-invert px-3 py-2 min-h-[120px] focus:outline-none [&_.ProseMirror]:min-h-[100px] [&_.ProseMirror]:outline-none [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:underline [&_.is-editor-empty]:before:content-[attr(data-placeholder)] [&_.is-editor-empty]:before:text-muted-foreground [&_.is-editor-empty]:before:float-left [&_.is-editor-empty]:before:pointer-events-none"
        data-testid="rte-editor"
      />
    </div>
  );
}
